import React from 'react';
import styles from './index.module.scss';
import { DatePicker, Radio } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn';
import locale from 'antd/es/date-picker/locale/zh_CN';
import { setPostImage } from '@images/imageUrls';

interface FormField {
  type: 'input' | 'image' | 'radio' | 'checkbox';
  title: string;
  name?: string;
  isRequired: boolean;
  options?: any[];
  editTitleStatus?: boolean;
  currentOptionEditIndex?: string | number;
  inputCharCount?: number; // 追踪输入的字符数
}

interface FormModalProps {
  visible: boolean;
  initialData?: {
    name: string;
    fields: FormField[];
    startAt?: string;
    endAt?: string;
  };
  onSave: (data: any) => void;
  onCancel: () => void;
  onClose: () => void;
  userCount?: number; // 已填写人数 0=未填写过
  hasFilledPermissions?: boolean; // 是否有已填写数据（控制是否可修改题目）
}

interface FormModalState {
  formTitle: string;
  formTitleStatus: boolean;
  formData: FormField[];
  startAt: Dayjs | null;
  endAt: Dayjs | null;
  questionTypePopupStatus: boolean;
  typeList: Array<{
    type: 'input' | 'radio' | 'checkbox' | 'image';
    value: string;
    key: number;
    icon: string;
    active: boolean;
  }>;
  addCurrentQuestionType: string;
  addCurrentQuestionIndex: number;
  inputRef: any[];
  formInfo: any[];
  formCreateAt: string;
  fatherIndex: string;
  animatingIndex?: number;
  animatingAdjacentIndex?: number;
  animationType?: 'up' | 'down';
  swapCompletedIndex?: number;
}

class FormModal extends React.Component<FormModalProps, FormModalState> {
  formTitleRef = React.createRef<HTMLInputElement>();
  containerRef = React.createRef<HTMLDivElement>();
  formLock = false;

  constructor(props: FormModalProps) {
    super(props);
    this.state = {
      formTitle: '',
      formTitleStatus: false,
      formData: [],
      startAt: null,
      endAt: null,
      questionTypePopupStatus: false,
      typeList: [
        { type: 'radio', value: '单选题', key: 0, icon: '/iqoo/radio-type', active: false },
        { type: 'checkbox', value: '多选题', key: 0, icon: '/iqoo/checkbox-type', active: false },
        { type: 'input', value: '问答题', key: 0, icon: '/iqoo/input-type', active: false },
        { type: 'image', value: '图片', key: 0, icon: '/iqoo/image-type', active: false },
      ],
      addCurrentQuestionType: '',
      addCurrentQuestionIndex: 0,
      inputRef: [],
      formInfo: [],
      formCreateAt: '',
      fatherIndex: '',
      animatingIndex: undefined,
      animatingAdjacentIndex: undefined,
      animationType: undefined,
      swapCompletedIndex: undefined,
    };
  }

  componentDidMount() {
    const { initialData } = this.props;
    if (initialData) {
      const originalArray = JSON.parse(JSON.stringify(initialData.fields || []));
      const transformedArray = originalArray.map((item: any) => {
        if (item.type === 'radio' || item.type === 'checkbox') {
          return {
            isRequired: item.isRequired,
            name: item.name || item.title, // 兼容 name 和 title 字段
            title: item.title || item.name, // 兼容 title 和 name 字段
            type: item.type,
            values: item.values,
            options: item.options?.map((option: any) => {
              // 支持字符串和对象两种格式
              return { title: typeof option === 'string' ? option : option.title, editItemStatus: false };
            }) || [],
            inputCharCount: (item.title || item.name || '').length,
          };
        }
        // 处理 input 和 image 类型
        return {
          ...item,
          name: item.name || item.title,
          title: item.title || item.name,
          inputCharCount: (item.title || item.name || '').length,
        };
      });
      this.setState({
        formData: transformedArray,
        formTitle: initialData.name || '',
        startAt: initialData.startAt ? dayjs(initialData.startAt) : null,
        endAt: initialData.endAt ? dayjs(initialData.endAt) : null,
      });
    }
  }

  componentDidUpdate(prevProps: FormModalProps) {
    // 监听 initialData 变化，重新加载表单数据
    if (this.props.initialData !== prevProps.initialData && this.props.visible) {
      const { initialData } = this.props;
      if (initialData) {
        const originalArray = JSON.parse(JSON.stringify(initialData.fields || []));
        const transformedArray = originalArray.map((item: any) => {
          if (item.type === 'radio' || item.type === 'checkbox') {
            return {
              isRequired: item.isRequired,
              name: item.name || item.title,
              title: item.title || item.name,
              type: item.type,
              values: item.values,
              options: item.options?.map((option: any) => {
                return { title: typeof option === 'string' ? option : option.title, editItemStatus: false };
              }) || [],
              inputCharCount: (item.title || item.name || '').length,
            };
          }
          return {
            ...item,
            name: item.name || item.title,
            title: item.title || item.name,
            inputCharCount: (item.title || item.name || '').length,
          };
        });
        this.setState({
          formData: transformedArray,
          formTitle: initialData.name || '',
          startAt: initialData.startAt ? dayjs(initialData.startAt) : null,
          endAt: initialData.endAt ? dayjs(initialData.endAt) : null,
        });
      }
    }

    setTimeout(() => {
      if (this.containerRef.current && this.formLock) {
        this.containerRef.current.scrollTop = this.containerRef.current.scrollHeight;
      }
    }, 0);
  }

  changeFormTitleStatus = () => {
    this.formLock = false;
    const { formTitleStatus } = this.state;
    this.setState({ formTitleStatus: !formTitleStatus }, () => {
      this.formTitleRef.current?.focus();
    });
  };

  formTitleInputBlur = (text: string) => {
    this.formTitleRef.current?.blur();
    this.setState({ formTitleStatus: false });
    if (!text || text === '表单标题') {
      this.setState({ formTitle: '表单标题' });
    } else {
      this.setState({ formTitle: text });
    }
  };

  setFormTitle = (text: string) => {
    this.setState({ formTitle: text });
  };

  handleStartAtChange = (date: Dayjs | null) => {
    this.setState({ startAt: date });
  };

  handleEndAtChange = (date: Dayjs | null) => {
    this.setState({ endAt: date });
  };

  openQuestionTypePopup = () => {
    this.formLock = true;
    this.setState({ questionTypePopupStatus: true });
  };

  closeQuestionTypePopup = () => {
    this.setState({ questionTypePopupStatus: false });
  };

  setQuestionTypePopup = (type: string, key: number) => {
    this.setState({ addCurrentQuestionType: type, addCurrentQuestionIndex: key }, () => {
      this.submitQuestionTypePopup();
    });
  };

  submitQuestionTypePopup = () => {
    const { typeList, addCurrentQuestionType, addCurrentQuestionIndex, formData, inputRef } = this.state;
    const newTypeList = [...typeList];
    newTypeList[addCurrentQuestionIndex].active = !newTypeList[addCurrentQuestionIndex].active;
    this.setState({ typeList: newTypeList });

    let item: any = {};
    if (!addCurrentQuestionType) {
      return;
    }

    if (addCurrentQuestionType === 'input' || addCurrentQuestionType === 'image') {
      item = {
        type: newTypeList[addCurrentQuestionIndex].type,
        title: newTypeList[addCurrentQuestionIndex].value,
        isRequired: false,
        options: '',
        editTitleStatus: false,
        inputCharCount: 0,
      };
    } else {
      item = {
        type: newTypeList[addCurrentQuestionIndex].type,
        title: newTypeList[addCurrentQuestionIndex].value,
        isRequired: false,
        options: [
          { title: '选项', editItemStatus: false },
          { title: '选项', editItemStatus: false },
        ],
        currentOptionEditIndex: '',
        editTitleStatus: false,
        inputCharCount: 0,
      };
    }

    inputRef.push('');
    const newFormData = [...formData, item];
    setTimeout(() => {
      this.setState({ questionTypePopupStatus: false, formData: newFormData }, () => {
        this.setState({ addCurrentQuestionType: '' }, () => {
          setTimeout(() => {
            this.formLock = false;
          }, 100);
        });
      });
    }, 0);
  };

  copyFormItem = (item: FormField, index: number) => {
    const { formData } = this.state;
    const itemCopy = JSON.parse(JSON.stringify(item));
    itemCopy.editTitleStatus = false;
    itemCopy.currentOptionEditIndex = '';
    formData.splice(index + 1, 0, itemCopy);
    this.setState({ formData: [...formData] });
  };

  deleteFormItem = (index: number) => {
    const { formData } = this.state;
    formData.splice(index, 1);
    this.setState({ formData: [...formData] });
  };

  moveUpFormItem = (item: FormField, index: number) => {
    const { formData } = this.state;
    // 显示两个元素交换的动画
    this.setState({ animatingIndex: index, animatingAdjacentIndex: index - 1, animationType: 'up' });
    
    // 等动画完成后再实际交换数据
    setTimeout(() => {
      formData[index] = formData.splice(index - 1, 1, formData[index])[0];
      this.setState({ 
        formData: [...formData],
        animatingIndex: undefined, 
        animatingAdjacentIndex: undefined, 
        animationType: undefined,
        swapCompletedIndex: index - 1
      });
      // 高亮效果结束
      setTimeout(() => {
        this.setState({ swapCompletedIndex: undefined });
      }, 500);
    }, 350);
  };

  moveDownFormItem = (item: FormField, index: number) => {
    const { formData } = this.state;
    // 显示两个元素交换的动画
    this.setState({ animatingIndex: index, animatingAdjacentIndex: index + 1, animationType: 'down' });
    
    // 等动画完成后再实际交换数据
    setTimeout(() => {
      formData[index] = formData.splice(index + 1, 1, formData[index])[0];
      this.setState({ 
        formData: [...formData],
        animatingIndex: undefined, 
        animatingAdjacentIndex: undefined, 
        animationType: undefined,
        swapCompletedIndex: index + 1
      });
      // 高亮效果结束
      setTimeout(() => {
        this.setState({ swapCompletedIndex: undefined });
      }, 500);
    }, 350);
  };

  editFormItemTitle = (index: number) => {
    this.formLock = false;
    const { formData } = this.state;
    formData[index].editTitleStatus = true;
    this.setState({ formData, fatherIndex: index.toString() });
  };

  onClearSelectStatus = (type: string, key: number) => {
    const { formData } = this.state;
    const arrayList = JSON.parse(JSON.stringify(formData));
    const updatedArray = arrayList.map((item: any, index: number) => {
      return {
        ...item,
        currentOptionEditIndex: (type === 'title' || type === 'clear' || type === 'clearForm') ? '' : key,
        editTitleStatus: type === 'title' && index === key ? true : false,
      };
    });

    if (type === 'clear' || type === 'clearForm') {
      this.setState({
        fatherIndex: '',
      });
    }
    setTimeout(() => {
      this.setState({
        formData: updatedArray,
        questionTypePopupStatus: type === 'clearForm' ? true : false,
      });
    }, 0);
  };

  setFormItemTitle = (text: string, index: number) => {
    const { formData } = this.state;
    formData[index].title = text;
    formData[index].inputCharCount = text.length;
    this.setState({ formData: [...formData] });
  };

  changeFormItemTitleStatus = (text: string, index: number) => {
    const { formData } = this.state;
    if (!formData[index].title) {
      return false;
    }
    formData[index].editTitleStatus = false;
    this.setState({ formData: [...formData] });
  };

  addOptions = (index: number) => {
    const { formData } = this.state;
    const item = { title: '选项' };
    if (Array.isArray(formData[index].options)) {
      formData[index].options!.push(item);
      this.setState({ formData: [...formData] });
    }
  };

  editOptionItem = (index: number, key: number) => {
    this.formLock = false;
    const { formData } = this.state;
    const updatedArray = formData.map((item: any, mapIndex: number) => {
      return {
        ...item,
        currentOptionEditIndex: index === mapIndex ? key : '',
        editTitleStatus: false,
      };
    });
    this.setState({ formData: updatedArray, fatherIndex: index.toString() });
  };

  setRadioOptionText = (text: string, index: number, key: number) => {
    const { formData } = this.state;
    if (Array.isArray(formData[index].options)) {
      formData[index].options![key].title = text;
      this.setState({ formData: [...formData] });
    }
  };

  changeRadioOptionStatus = (index: number, key: number) => {
    const { formData } = this.state;
    if (!formData[index].options || !Array.isArray(formData[index].options)) {
      return false;
    }
    const options = formData[index].options as any[];
    if (!options[key].title) {
      return false;
    }
    formData[index].currentOptionEditIndex = '';
    this.setState({ formData: [...formData] });
  };

  // 更新字符计数
  updateInputCharCount = (index: number, value: string) => {
    const { formData } = this.state;
    formData[index].inputCharCount = value.length;
    this.setState({ formData: [...formData] });
  };

  setMustAnswer = (index: number) => {
    const { formData } = this.state;
    formData[index].isRequired = !formData[index].isRequired;
    this.setState({ formData: [...formData] });
  };

  // 切换单选/多选类型
  toggleQuestionType = (index: number, type: 'radio' | 'checkbox') => {
    const { formData } = this.state;
    if (formData[index].type === type) return;
    formData[index].type = type;
    this.setState({ formData: [...formData] });
  };

  radioOptionAdd = (index: number, key: number) => {
    const { formData } = this.state;
    const item = { title: '选项' };
    if (Array.isArray(formData[index].options)) {
      formData[index].options!.splice(key + 1, 0, item);
      this.setState({ formData: [...formData] });
    }
  };

  radioOptionReduce = (index: number, key: number) => {
    const { formData } = this.state;
    if (Array.isArray(formData[index].options)) {
      formData[index].options!.splice(key, 1);
      this.setState({ formData: [...formData] });
    }
  };

  submitFormData = () => {
    const { onSave, onCancel } = this.props;
    const { formData, formTitle, startAt, endAt } = this.state;
    const formDataObj: any = JSON.parse(JSON.stringify(formData));

    for (let i = 0; i < formDataObj.length; i++) {
      delete formDataObj[i].editTitleStatus;
      delete formDataObj[i].currentOptionEditIndex;

      if (Array.isArray(formDataObj[i].options)) {
        const optionsArr: string[] = [];
        for (let k = 0; k < formDataObj[i].options.length; k++) {
          delete formDataObj[i].options[k].editItemStatus;
          optionsArr.push(formDataObj[i].options[k].title);
        }
        formDataObj[i].options = optionsArr;
      }
    }

    const formdataObject = {
      formTitle: formTitle || '',
      form: formDataObj,
      startAt: startAt ? startAt.format('YYYY-MM-DD HH:mm') : '',
      endAt: endAt ? endAt.format('YYYY-MM-DD HH:mm') : '',
    };

    if (formdataObject.form.length === 0 && !formdataObject.formTitle) {
      onCancel && onCancel();
      onSave({
        formTitle: formTitle || '',
        form: [],
        startAt: startAt ? startAt.format('YYYY-MM-DD HH:mm') : '',
        endAt: endAt ? endAt.format('YYYY-MM-DD HH:mm') : '',
      });
      return;
    }

    onSave(formdataObject);
  };

  render() {
    const { visible, onCancel, onClose, userCount = 0 } = this.props;
    const { fatherIndex, formTitle, formData, questionTypePopupStatus, typeList, addCurrentQuestionType, startAt, endAt } = this.state;

    // 权限控制：userCount > 0 表示已有人填写，只读模式
    const isFormReadOnly = userCount > 0;
    const canDeleteQuestions = !isFormReadOnly;
    const canAddQuestions = !isFormReadOnly;

    if (!visible) return null;

    return (
      <>
        <div className={styles.alertMask} onClick={onCancel} />
        <div className={styles.alertBox} onClick={() => this.onClearSelectStatus('clear', 0)}>
          <div className={styles.formHeader}>
            表单填写
            <div className={styles.formClose} onClick={() => onClose && onClose()} />
          </div>
          <div className={styles.formContent}>
              
              {/* 表单标题 */}
              <div className={styles.formFieldRow}>
                <div className={styles.formFieldLabel}>
                  <span className={styles.required}>*</span>表单标题
                </div>
                <div className={styles.formFieldContent}>
                  <input
                    ref={this.formTitleRef}
                    value={formTitle}
                    placeholder="请输入表单标题"
                    maxLength={40}
                    onChange={(e) => {
                      if (isFormReadOnly) return;
                      this.setFormTitle(e.target.value);
                    }}
                    disabled={isFormReadOnly}
                    className={styles.formTitleInput}
                  />
                  <span className={styles.charCount}>{formTitle?.length || 0}/40</span>
                </div>
              </div>

              {/* 表单收集时间 */}
              <div className={styles.formFieldRow}>
                <div className={styles.formFieldLabel}>
                  <span className={styles.required}>*</span>表单收集时间
                </div>
                <div className={styles.formFieldContent}>
                  <div className={styles.dateRangePicker}>
                    <DatePicker
                      value={startAt}
                      onChange={this.handleStartAtChange}
                      showTime={{ format: 'HH:mm' }}
                      format="YYYY-MM-DD HH:mm"
                      placeholder="请选择开始时间"
                      locale={locale}
                      disabled={isFormReadOnly}
                      className={styles.datePicker}
                    />
                    <span className={styles.dateRangeArrow}>→</span>
                    <DatePicker
                      value={endAt}
                      onChange={this.handleEndAtChange}
                      showTime={{ format: 'HH:mm' }}
                      format="YYYY-MM-DD HH:mm"
                      placeholder="请选择截止时间"
                      locale={locale}
                      disabled={isFormReadOnly}
                      className={styles.datePicker}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formScrollBox} ref={this.containerRef}>
                {formData?.map((h: any, i: number) => {
                  const { animatingIndex, animatingAdjacentIndex, animationType, swapCompletedIndex } = this.state;
                  let animationClass = '';
                  if (animatingIndex === i) {
                    animationClass = animationType === 'up' ? styles.swapUpAnimation : styles.swapDownAnimation;
                  } else if (animatingAdjacentIndex === i) {
                    animationClass = animationType === 'up' ? styles.swapUpAdjacentAnimation : styles.swapDownAdjacentAnimation;
                  } else if (swapCompletedIndex === i) {
                    animationClass = styles.swapCompleted;
                  }
                  return (
                    <div className={classNames(styles.formChild, animationClass)} key={i}>
                      <div className={classNames(styles.backgroundNonetBox, fatherIndex === i.toString() ? styles.backgroundSelectBox : styles.backgroundBox)}>
                        <div className={styles.formChildHeader}>
                          <div
                            className={styles.formChildTitleBox}
                            onClick={(e) => {
                              if (isFormReadOnly) return;
                              e.stopPropagation();
                              this.formLock = false;
                              this.onClearSelectStatus('title', i);
                              this.editFormItemTitle(i);
                            }}
                          >
                              {h.isRequired && <span className={styles.requireStar}>*</span>}
                            <div className={styles.index}>
                              {i + 1}.
                            </div>
                            {!h.editTitleStatus && (
                              <div className={styles.formChildTitleCon}>
                                <div className={styles.formChildTitle}>{h.title || (h.type === 'radio' || h.type === 'checkbox' ? '选择题' : h.type === 'input' ? '问答题' : '图片')}</div>
                                <span className={styles.inputCharCount}>{h.inputCharCount || 0}/200</span>
                              </div>
                            )}
                            {h.editTitleStatus && fatherIndex === i.toString() && !isFormReadOnly && (
                              <div className={styles.formChildTitleCon}>
                                <input
                                  value={h.title}
                                  placeholder="请输入选项"
                                  maxLength={200}
                                  onChange={(e) => {
                                    this.setFormItemTitle(e.target.value, i);
                                    this.updateInputCharCount(i, e.target.value);
                                  }}
                                  onBlur={(e) => this.changeFormItemTitleStatus(e.target.value, i)}
                                  className={styles.formChildTitleInput}
                                />
                                <span className={styles.inputCharCount}>{h.inputCharCount || 0}/200</span>
                              </div>
                            )}
                          </div>
                          {/* 单选/多选切换 */}
                          {(h.type === 'radio' || h.type === 'checkbox') && (
                            <div className={styles.questionTypeSwitch} onClick={(e) => e.stopPropagation()}>
                              <Radio.Group 
                                value={h.type}
                                onChange={(e) => {
                                  if (!canDeleteQuestions) return;
                                  this.toggleQuestionType(i, e.target.value);
                                }}
                                size="small"
                                disabled={!canDeleteQuestions}
                              >
                                <Radio value="radio">单选</Radio>
                                <Radio value="checkbox">多选</Radio>
                              </Radio.Group>
                            </div>
                          )}
                        </div>
                        {h.type === 'input' && (
                          <div className={styles.formChildContent}>
                            <div className={styles.formChildTextarea}>请输入</div>
                            {/* <span className={styles.inputCharCount}>0/200</span> */}
                          </div>
                        )}

                        {h.type === 'image' && (
                          <div className={styles.formChildContent}>
                            <div className={styles.upload}>
                              <img src={setPostImage.addPhoto} alt="upload" />
                            </div>
                          </div>
                        )}
                        {(h.type === 'radio' || h.type === 'checkbox') && (
                          <div className={styles.formChildContent}>
                            {h?.options?.map((m: any, n: number) => {
                              return (
                                <div
                                  className={styles.radioItemBox}
                                  onClick={(e) => {
                                    if (isFormReadOnly) return;
                                    e.stopPropagation();
                                    this.formLock = false;
                                    this.editOptionItem(i, n);
                                  }}
                                  key={n}
                                >
                                  <div className={classNames(styles.radioEnterBox, h.currentOptionEditIndex === n && fatherIndex === i.toString() ? styles.radioEnter : '')}>
                                    <img src={setPostImage.atCheckout} alt="radio" className={styles.radioIcon} />
                                    {h.currentOptionEditIndex !== n && <div className={styles.radioItemText}>{m.title}</div>}
                                    {h.currentOptionEditIndex === n && !isFormReadOnly && (
                                      <input
                                        value={m.title}
                                        placeholder=""
                                        onChange={(e) => {
                                          this.setRadioOptionText(e.target.value, i, n);
                                        }}
                                        onBlur={(e) => this.changeRadioOptionStatus(i, n)}
                                        className={styles.radioItemInput}
                                      />
                                    )}
                                  </div>
                                  {h.currentOptionEditIndex === n && !isFormReadOnly && (
                                    <div className={styles.optionBtns}>
                                      <img
                                        src={setPostImage.jiaSet}
                                        alt="add"
                                        className={classNames(styles.radioAdd, !canDeleteQuestions ? styles.optionBtnDisabled : '')}
                                        onClick={(e) => {
                                          if (!canDeleteQuestions) return;
                                          e.stopPropagation();
                                          this.formLock = false;
                                          this.radioOptionAdd(i, n);
                                        }}
                                        title={!canDeleteQuestions ? '有人填写后不可新增选项' : ''}
                                      />
                                      <img
                                        src={setPostImage.deleteSet}
                                        alt="delete"
                                        className={classNames(styles.radioReduce, !canDeleteQuestions ? styles.optionBtnDisabled : '')}
                                        onClick={(e) => {
                                          if (!canDeleteQuestions) return;
                                          e.stopPropagation();
                                          this.formLock = false;
                                          this.radioOptionReduce(i, n);
                                        }}
                                        title={!canDeleteQuestions ? '有人填写后不可删除选项' : ''}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {!isFormReadOnly && (
                          <div className={styles.formOperaBox}>
                            <div className={styles.formOperaCon}>
                              <div className={styles.formOperaLeft}>
                                {(h.type === 'radio' || h.type === 'checkbox') && (
                                  <div
                                    className={classNames(styles.addOptions, !canDeleteQuestions ? styles.addOptionsDisabled : '')}
                                    onClick={(e) => {
                                      if (!canDeleteQuestions) return;
                                      e.stopPropagation();
                                      this.formLock = false;
                                      this.addOptions(i);
                                    }}
                                    title={!canDeleteQuestions ? '有人填写后不可新增选项' : ''}
                                  >
                                    <img src={setPostImage.jiaSet} alt="add" className={styles.addIcon} />
                                    <div className={styles.addText}>添加选项</div>
                                  </div>
                                )}
                              </div>
                              <div className={styles.formOperaRight}>
                                <div className={styles.mustAnswer} onClick={(e) => {
                                      e.stopPropagation();
                                      this.formLock = false;
                                      this.setMustAnswer(i);
                                    }}>
                                  <img 
                                    src={h.isRequired 
                                      ? setPostImage.atCheckoutActive 
                                      : setPostImage.atCheckout} 
                                    alt="checkbox" 
                                    className={styles.mustAnswerIcon}
                                  />
                                  <span>必答</span>
                                </div>
                                <div
                                  className={classNames(styles.formOpera, !canDeleteQuestions ? styles.operaDisabled : '')}
                                  onClick={(e) => {
                                    if (!canDeleteQuestions) return;
                                    e.stopPropagation();
                                    this.formLock = false;
                                    this.copyFormItem(h, i);
                                  }}
                                  title={!canDeleteQuestions ? '有人填写后不可复制题目' : ''}
                                >
                                  <img src={setPostImage.copySet} alt="copy" className={styles.operaIcon} />
                                  <span>复制</span>
                                </div>
                                <div
                                  className={classNames(styles.formOpera, !canDeleteQuestions ? styles.operaDisabled : '')}
                                  onClick={(e) => {
                                    if (!canDeleteQuestions) return;
                                    e.stopPropagation();
                                    this.formLock = false;
                                    this.deleteFormItem(i);
                                  }}
                                  title={!canDeleteQuestions ? '有人填写后不可删除题目' : ''}
                                >
                                  <img src={setPostImage.deleteSet} alt="delete" className={styles.operaIcon} />
                                  <span>删除</span>
                                </div>
                                {i !== 0 && (
                                  <div
                                    className={styles.formOpera}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      this.formLock = false;
                                      this.moveUpFormItem(h, i);
                                    }}
                                  >
                                    <img src={setPostImage.upSet} alt="up" className={styles.operaIcon} />
                                    <span>上移</span>
                                  </div>
                                )}
                                {i + 1 !== formData.length && (
                                  <div
                                    className={styles.formOpera}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      this.formLock = false;
                                      this.moveDownFormItem(h, i);
                                    }}
                                  >
                                    <img src={setPostImage.downSet} alt="down" className={styles.operaIcon} />
                                    <span>下移</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {!isFormReadOnly && (
                  <div className={styles.addQuestionTypeBox}>
                    <span
                      className={classNames(styles.addQuestionType, !canAddQuestions ? styles.addQuestionTypeDisabled : '')}
                      onClick={(e) => {
                        if (!canAddQuestions) return;
                        e.stopPropagation();
                        this.openQuestionTypePopup();
                        this.onClearSelectStatus('clearForm', 0);
                      }}
                      title={!canAddQuestions ? '有人填写后不可添加题目' : ''}
                    >
                      添加题目类型
                    </span>
                  </div>
                )}

                {questionTypePopupStatus && (
                  <div className={styles.questionTypePopupMask}>
                    <div className={styles.questionTypePopup}>
                      <div className={styles.popupContent}>
                        {typeList?.map((v: any, k: number) => {
                          return (
                            <div
                              className={classNames(styles.typeChild, v.type === addCurrentQuestionType ? styles.typeActive : '')}
                              onClick={(e) => {
                                e.stopPropagation();
                                this.setQuestionTypePopup(v.type, k);
                              }}
                              key={k}
                            >
                              <div className={styles.typeIcon}>
                                {v.type === 'radio' && <img src={setPostImage.danxuanSet} alt="radio" className={styles.typeIconImg} />}
                                {v.type === 'checkbox' && <img src={setPostImage.duoxuanSet} alt="checkbox" className={styles.typeIconImg} />}
                                {v.type === 'input' && <img src={setPostImage.wendaSet} alt="input" className={styles.typeIconImg} />}
                                {v.type === 'image' && <img src={setPostImage.photoSet} alt="photo-upload" className={styles.typeIconImg} />}
                              </div>
                              <div className={styles.typeText}>{v.value}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {!isFormReadOnly && (
              <div className={styles.formBtnBox}>
                <button className={styles.stagingBtn} onClick={() => onCancel && onCancel()}>
                  暂存
                </button>
                <button 
                  className={styles.editingBtn}
                  disabled={!formTitle || !startAt || !endAt || formData.length === 0}
                  onClick={() => this.submitFormData()}
                >
                  完成编辑
                </button>
              </div>
            )}
        </div>
      </>
    );
  }
}

export default FormModal;
